import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'

import FilterBox, { PreviewLine } from './FilterBox'
import IncludeExcludeToggle from './IncludeExcludeToggle'
import { Heading } from './SearchFilterColumn'
import SegmentedRadio from './SegmentedRadio'
import { MUTED_TEXT, NAVY, NAVY_TINT, WARNING_TEXT, visuallyHidden } from './styles'
import {
  MAX_KEYWORDS,
  MAX_KEYWORD_LENGTH,
  type AnyAll,
  type DatabaseSearchFilters,
} from '@/models/DatabaseSearch'

/** Lower-case and strip macrons, so "Māori" and "maori" are the same keyword. */
function foldKeyword(text: string) {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

/**
 * Adds every comma-separated phrase in `raw` to `keywords`, tidying spaces and
 * skipping any already present.
 */
/** Keywords that couldn't be added, and why. */
export interface KeywordsSkipped {
  tooLong: string[]
  overLimit: string[]
}

export function addKeywordsWithSkips(
  keywords: string[],
  raw: string,
): { keywords: string[]; skipped: KeywordsSkipped } {
  const next = [...keywords]
  const skipped: KeywordsSkipped = { tooLong: [], overLimit: [] }
  for (const part of raw.split(',')) {
    const chip = part.replace(/\s+/g, ' ').trim()
    if (!chip || next.some((k) => foldKeyword(k) === foldKeyword(chip))) continue
    if (chip.length > MAX_KEYWORD_LENGTH) skipped.tooLong.push(chip)
    else if (next.length >= MAX_KEYWORDS) skipped.overLimit.push(chip)
    else next.push(chip)
  }
  return { keywords: next, skipped }
}

export function addKeywords(keywords: string[], raw: string): string[] {
  return addKeywordsWithSkips(keywords, raw).keywords
}

/** What to tell the user about keywords that were left out, or ''. */
export function skippedKeywordsNote({ tooLong, overLimit }: KeywordsSkipped): string {
  const notes = []
  if (tooLong.length) {
    notes.push(`Keywords can be up to ${MAX_KEYWORD_LENGTH} characters; a longer one wasn't added.`)
  }
  if (overLimit.length) {
    notes.push(
      `You can add up to ${MAX_KEYWORDS} keywords; ${overLimit.map((k) => `"${k}"`).join(', ')} ${overLimit.length === 1 ? "wasn't" : "weren't"} added.`,
    )
  }
  return notes.join(' ')
}

export function keywordsAndTagsPreview(
  filters: DatabaseSearchFilters,
): ReactNode | null {
  const lines: ReactNode[] = []
  if (filters.generalist === 'exclude') {
    lines.push(<PreviewLine key="g" label="Generalist grants excluded" />)
  }
  if (filters.keywords.length) {
    lines.push(
      <PreviewLine
        key="k"
        label={`Keywords (${filters.keyword_mode})`}
        chips={filters.keywords}
      />,
    )
  }
  if (filters.include_tags.length) {
    lines.push(
      <PreviewLine
        key="i"
        label={`Include tags (${filters.include_tag_mode})`}
        chips={filters.include_tags}
      />,
    )
  }
  if (filters.exclude_tags.length) {
    lines.push(
      <PreviewLine key="e" label="Exclude tags" chips={filters.exclude_tags} />,
    )
  }
  return lines.length ? <>{lines}</> : null
}

function SubHeading({ children }: { children: ReactNode }) {
  return (
    <Typography
      component="h3"
      sx={{ fontSize: '15px', fontWeight: 500, color: NAVY, mt: 2, mb: 0.25, '&:first-of-type': { mt: 0.5 } }}
    >
      {children}
    </Typography>
  )
}

function Helper({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <Typography id={id} sx={{ fontSize: '14px', color: MUTED_TEXT, mb: 0.75 }}>
      {children}
    </Typography>
  )
}

function AnyAllSwitch({
  label,
  value,
  anyLabel,
  allLabel,
  onChange,
}: {
  label: string
  value: AnyAll
  anyLabel: string
  allLabel: string
  onChange: (value: AnyAll) => void
}) {
  const helperId = useId()
  return (
    <Box sx={{ mt: 1 }}>
      <Helper id={helperId}>Opportunities need:</Helper>
      <SegmentedRadio
        label={label}
        describedBy={helperId}
        value={value}
        options={[
          { value: 'any', label: anyLabel },
          { value: 'all', label: allLabel },
        ]}
        onChange={onChange}
      />
    </Box>
  )
}

function TagChips({
  label,
  tags,
  selected,
  blocked,
  blockedTip,
  onToggle,
}: {
  label: string
  tags: string[]
  selected: string[]
  blocked: string[]
  blockedTip: string
  onToggle: (tag: string) => void
}) {
  return (
    <Box role="group" aria-label={label} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {tags.map((tag) => {
        const on = selected.includes(tag)
        const off = blocked.includes(tag)
        if (off) {
          // Greyed rather than disabled: a disabled chip loses its role and
          // fires no hover or focus events, so the tooltip would never show.
          return (
            <Tooltip key={tag} title={blockedTip} describeChild>
              <Chip
                label={tag}
                size="small"
                variant="outlined"
                role="button"
                tabIndex={0}
                aria-disabled="true"
                aria-pressed={false}
                // #6b7280 keeps 4.8:1; the dashed border marks it unavailable.
                sx={{ borderColor: '#9ca3af', borderStyle: 'dashed', color: '#6b7280', cursor: 'not-allowed' }}
              />
            </Tooltip>
          )
        }
        return (
          <Chip
            key={tag}
            label={tag}
            size="small"
            clickable
            aria-pressed={on}
            onClick={() => onToggle(tag)}
            variant={on ? 'filled' : 'outlined'}
            sx={{
              borderColor: NAVY,
              color: on ? '#fff' : NAVY,
              bgcolor: on ? NAVY : '#fff',
              '&.MuiChip-clickable:hover': { bgcolor: on ? NAVY : NAVY_TINT },
              '&.Mui-focusVisible': { outline: `2px solid ${NAVY}`, outlineOffset: 2 },
            }}
          />
        )
      })}
    </Box>
  )
}

interface KeywordsAndTagsProps {
  filters: DatabaseSearchFilters
  onChange: (patch: Partial<DatabaseSearchFilters>) => void
  tags: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Text typed but not yet turned into a chip; the page commits it on Search. */
  keywordDraft: string
  onKeywordDraftChange: (draft: string) => void
}

export default function KeywordsAndTags({
  filters,
  onChange,
  tags,
  open,
  onOpenChange,
  keywordDraft,
  onKeywordDraftChange,
}: KeywordsAndTagsProps) {
  const chipsRef = useRef<HTMLUListElement>(null)
  const keywordInputRef = useRef<HTMLInputElement>(null)
  const keywordHelpId = useId()
  // Read out by a hidden live region as keywords come and go.
  const [keywordNews, setKeywordNews] = useState('')
  // Shown, and read out, when a keyword is too long or over the limit.
  const [keywordNote, setKeywordNote] = useState('')
  // A live region only speaks when its text changes. A message that matches
  // what the region already shows gets a no-break space, so it is read out
  // again; the next repeat drops it, and so on.
  const fresh = (message: string) => (shown: string) =>
    message && shown === message ? `${message}\u00a0` : message
  // A removed keyword takes its button with it. Move focus to the button now
  // in its place, else the one before it, else the keyword box.
  const [focusChip, setFocusChip] = useState<number | null>(null)
  useEffect(() => {
    if (focusChip === null) return
    const buttons = chipsRef.current?.querySelectorAll<HTMLElement>('[data-keyword-remove]') ?? []
    const target = buttons[Math.min(focusChip, buttons.length - 1)] ?? keywordInputRef.current
    target?.focus()
    setFocusChip(null)
  }, [focusChip, filters.keywords])

  const countText = (n: number) => `${n} ${n === 1 ? 'keyword' : 'keywords'}.`

  function commitDraft(draft: string) {
    const { keywords: next, skipped } = addKeywordsWithSkips(filters.keywords, draft)
    const added = next.slice(filters.keywords.length)
    const typed = draft.split(',').some((part) => part.trim())
    if (added.length) onChange({ keywords: next })
    setKeywordNote(fresh(skippedKeywordsNote(skipped)))
    if (added.length) {
      setKeywordNews(fresh(`Added ${added.map((k) => `"${k}"`).join(', ')}. ${countText(next.length)}`))
    } else if (typed && !skipped.tooLong.length && !skipped.overLimit.length) {
      setKeywordNews(fresh(`Already added. ${countText(next.length)}`))
    }
    onKeywordDraftChange('')
  }

  function removeKeyword(keyword: string, index: number) {
    const next = filters.keywords.filter((k) => k !== keyword)
    onChange({ keywords: next })
    setKeywordNews(fresh(`Removed "${keyword}". ${countText(next.length)}`))
    setKeywordNote('')
    setFocusChip(index)
  }

  function toggleTag(list: 'include_tags' | 'exclude_tags', tag: string) {
    const current = filters[list]
    onChange({
      [list]: current.includes(tag)
        ? current.filter((t) => t !== tag)
        : [...current, tag].sort((a, b) => a.localeCompare(b)),
    })
  }

  return (
    <>
      <Heading>Keywords &amp; tags</Heading>
      <Helper>Filter by categories or keywords</Helper>
      <FilterBox
        noun="keywords and tags"
        open={open}
        onOpenChange={onOpenChange}
        preview={keywordsAndTagsPreview(filters)}
      >
        <SubHeading>Generalist grants</SubHeading>
        <IncludeExcludeToggle
          label="Generalist grants (tag: all)"
          value={filters.generalist}
          onChange={(value) => onChange({ generalist: value })}
        />

        <SubHeading>Keywords</SubHeading>
        <Helper id={keywordHelpId}>
          Type a word or phrase, then press Enter or a comma to add it. Matches
          whole words only.
        </Helper>
        {filters.keywords.length > 0 && (
          <Box
            ref={chipsRef}
            component="ul"
            aria-label="Keywords"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1, p: 0, listStyle: 'none' }}
          >
            {filters.keywords.map((keyword, index) => (
              <Box
                component="li"
                key={keyword}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  pl: 1.25,
                  borderRadius: 4,
                  bgcolor: 'rgba(0, 0, 0, 0.08)',
                  fontSize: '13px',
                }}
              >
                {keyword}
                <IconButton
                  size="small"
                  disableRipple
                  data-keyword-remove
                  aria-label={`Remove keyword ${keyword}`}
                  onClick={() => removeKeyword(keyword, index)}
                  sx={{
                    width: 24,
                    height: 24,
                    ml: 0.25,
                    color: MUTED_TEXT,
                    '&:hover': { color: NAVY },
                    '&.Mui-focusVisible': { outline: `2px solid ${NAVY}`, outlineOffset: -2 },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        <Box role="status" sx={visuallyHidden}>
          {keywordNews}
        </Box>
        <TextField
          size="small"
          fullWidth
          value={keywordDraft}
          placeholder="Type a keyword or phrase, then Enter or comma"
          inputRef={keywordInputRef}
          inputProps={{
            'aria-label': 'Keyword',
            'aria-describedby': keywordHelpId,
          }}
          onChange={(e) => {
            const value = e.target.value
            // A pasted list arrives with its commas in one change.
            if (value.includes(',')) commitDraft(value)
            else onKeywordDraftChange(value)
          }}
          onKeyDown={(e) => {
            // Enter also confirms an IME composition; that must not add a keyword.
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault()
              commitDraft(keywordDraft)
            }
          }}
          sx={{ bgcolor: '#fff' }}
        />
        <Typography role="status" sx={{ fontSize: '14px', color: WARNING_TEXT, mt: 0.5 }}>
          {keywordNote}
        </Typography>
        <AnyAllSwitch
          label="Keyword match"
          value={filters.keyword_mode}
          anyLabel="any keyword"
          allLabel="all keywords"
          onChange={(value) => onChange({ keyword_mode: value })}
        />

        <SubHeading>Include tags</SubHeading>
        <Helper>Filter by specific tags (select none to include all)</Helper>
        <TagChips
          label="Include tags"
          tags={tags}
          selected={filters.include_tags}
          blocked={filters.exclude_tags}
          blockedTip="Tag selected for exclusion"
          onToggle={(tag) => toggleTag('include_tags', tag)}
        />
        <AnyAllSwitch
          label="Tag match"
          value={filters.include_tag_mode}
          anyLabel="any selected tags"
          allLabel="all selected tags"
          onChange={(value) => onChange({ include_tag_mode: value })}
        />

        <SubHeading>Exclude tags</SubHeading>
        <Helper>Grants with any excluded tags will not appear in results</Helper>
        <Box sx={{ mb: 1 }}>
          <TagChips
            label="Exclude tags"
            tags={tags}
            selected={filters.exclude_tags}
            blocked={filters.include_tags}
            blockedTip="Tag selected for inclusion"
            onToggle={(tag) => toggleTag('exclude_tags', tag)}
          />
        </Box>
      </FilterBox>
    </>
  )
}
